<template>
  <div class="reporting management-page">
    <base-spinner v-if="loadingList" class="loader" />
    <div class="title" style="color: #383838; font-size: 24px;">Statements</div>
    <!-- Tabs -->
    <ul class="nav nav-tabs" id="payoutsTab" role="tablist">
      <li class="nav-item" role="presentation">
        <button
            class="nav-link active"
            id="payouts-tab"
            data-bs-toggle="tab"
            data-bs-target="#payouts"
            type="button"
            role="tab"
            aria-controls="payouts"
            aria-selected="true"
        >
          Payouts
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
            class="nav-link"
            id="statement-tab"
            data-bs-toggle="tab"
            data-bs-target="#statement"
            type="button"
            role="tab"
            aria-controls="statement"
            aria-selected="false"
        >
          Statement
        </button>
      </li>
    </ul>

    <div class="tab-content" id="payoutsTabContent" style="height: calc(100% - 80px);">
      <!-- Payouts Tab Content -->
      <div
          class="tab-pane fade show active"
          id="payouts"
          role="tabpanel"
          aria-labelledby="payouts-tab"
          style="height: 600px;"
      >
        <!-- Merchant Filter and Export Button Row -->
        <div class="row" style="margin-top: 25px; margin-bottom: 15px;">
          <div class="col-6">
            <div class="form-group row">
              <div class="col-sm-3 col-form-label">
                <label for="payoutsMerchantIdSelect">Merchant:</label>
              </div>
              <div class="col-sm-9">
                <select id="payoutsMerchantIdSelect" v-model="payoutsMerchantId" @change="fetchPayoutsData" class="form-select">
                  <option value="">All Merchants</option>
                  <option
                      v-for="option in merchantNameOptions"
                      :key="option.id"
                      :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="col-6 text-end">
            <!-- Export Button -->
            <button class="btn btn-primary" @click="exportPayoutsToExcel">
              Export Payouts to Excel
            </button>
          </div>
        </div>

        <!-- Payouts Grid -->
        <ag-grid-vue
            class="ag-theme-balham table mt-2"
            :columnDefs="columnDefinitionsMainTable"
            :rowData="payoutsRowData"
            :defaultColDef="defaultColDef"
            :pagination="true"
            :paginationPageSize="50"
            :sideBar="payoutsSideBar"
            @grid-ready="onPayoutsGridReady"
            @first-data-rendered="onPayoutsFirstDataRendered"
        ></ag-grid-vue>

        <!-- Modal for Settlement Group Details -->
        <base-modal v-model="showModal" :is-large="true" title="Settlement Group">
          <div class="row" style="margin-top: 35px">
            <div class="col-6">
              <p><strong>Payout ID:</strong> {{ selectedGroup.payout_details.id }}</p>
            </div>
            <div class="col-6">
              <p><strong>Processor:</strong> {{ selectedGroup.payout_details.processor }}</p>
            </div>
          </div>
          <div class="row" style="margin-top: 10px">
            <div class="col-6">
              <p><strong>Status:</strong> {{ selectedGroup.payout_details.status }}</p>
            </div>
            <div class="col-6">
              <p><strong>Payout Amount:</strong> {{ selectedGroup.payout_details.amount }}</p>
            </div>
          </div>
          <div v-if="role === 'admin'" class="row">
            <div class="col-12">
              <p><strong>Admin Line Item Total:</strong> {{ totalAuthAmount }}</p>
            </div>
          </div>
          <div class="row" style="margin-top: 10px">
            <div class="col-12">
              <ag-grid-vue
                  class="ag-theme-balham table mt-2"
                  :columnDefs="columnDefinitionsModal"
                  :rowData="settledTransactions"
                  :defaultColDef="defaultColDef"
                  :pagination="false"
                  :immutableData="true"
                  @grid-ready="onModalGridReady"
              ></ag-grid-vue>
            </div>
          </div>
        </base-modal>
      </div>

      <!-- Statement Tab Content -->
      <div
          class="tab-pane fade"
          id="statement"
          role="tabpanel"
          aria-labelledby="statement-tab"
          style="height: calc(100% - 80px);"
      >
        <div class="row" style="margin: 25px;">
          <div class="col-6">
            <div class="form-group row">
              <!-- Year-Month Dropdown -->
              <div class="col-sm-3 col-form-label">
                <label for="yearMonthSelect">Month:</label>
              </div>
              <div class="col-sm-9">
                <select id="yearMonthSelect" v-model="yearMonth" @change="fetchStatementData" class="form-select">
                  <option v-for="option in yearMonthOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>
            </div>
            <!-- Merchant ID Dropdown -->
            <div class="form-group row" style="margin-top: 10px;">
              <div class="col-sm-3 col-form-label">
                <label for="merchantIdSelect">Merchant:</label>
              </div>
              <div class="col-sm-9">
                <select id="merchantIdSelect" v-model="merchantId" @change="fetchStatementData" class="form-select">
                  <option value="">All Merchants</option>
                  <!-- Use the ID as the value, display the name -->
                  <option
                      v-for="option in merchantNameOptions"
                      :key="option.id"
                      :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <!-- Export + PDF Buttons -->
          <div class="col-6 text-end">
            <button class="btn btn-primary mb-2" @click="exportStatementToExcel">
              Export Statement to Excel
            </button>
            <button
                v-if="canDownloadPdf"
                class="btn btn-secondary mb-2 ms-2"
                @click="downloadStatementPDF"
            >
              PDF Statement
            </button>
            <p v-else class="text-muted small" style="margin-top: 10px;">
              Select a merchant and month for a downloadable PDF statement
            </p>
          </div>
        </div>
        <!-- Statement Grid -->
        <ag-grid-vue
            style="width: 100%; height: calc(100% - 80px);"
            :class="themeClass"
            :columnDefs="statementColumnDefs"
            :rowData="statementRowData"
            :defaultColDef="defaultColDef"
            :masterDetail="true"
            :detailRowHeight="getDetailRowHeight"
            :detailCellRenderer="StatementDetailRenderer"
            :detailCellRendererParams="detailCellRendererParams"
            :post-sort-rows="postSortStatementRows"
            @grid-ready="onStatementGridReady"
        ></ag-grid-vue>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, shallowRef, watch } from 'vue';
import BaseSpinner from '@/components/base/BaseSpinner.vue';
import { useStore } from 'vuex';
import AgGrid from '@/components/AgGrid.vue';
import BaseModal from '@/components/base/BaseModal.vue';
import { AgGridVue } from 'ag-grid-vue3';
import http from '@/services/http';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import StatementDetailRenderer from '@/components/statement/StatementDetailRenderer.vue';

import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.min.css';

const store = useStore();
const role = computed(() => store.getters.role);

// ----------------------------------------------------------------------
// MERCHANT ID (instead of merchant name). We'll call this merchantId.
// ----------------------------------------------------------------------
const merchantId = ref(''); // Holds currently selected merchant ID for statements
const payoutsMerchantId = ref(''); // Holds currently selected merchant ID for payouts
const merchantNameOptions = ref([]); // Array of { id, name }


const canDownloadPdf = computed(() => {
  return merchantId.value && yearMonth.value;
});

// Fetch the merchant names from /api/merchant-names
function fetchMerchantNames() {
  http.get('/api/merchant-names').then((response) => {
    // Suppose it returns something like [{ "123": "Merchant A" }, { "456": "Merchant B" }, ...]
    const rawNames = response.data.message.merchant_names;
    merchantNameOptions.value = rawNames.map((obj) => {
      const [id, name] = Object.entries(obj)[0];
      return { id, name };
    });
  });
}

const getDetailRowHeight = (params) => {
  const data = params.data;
  const transactionRows = data.daily_transactions ? data.daily_transactions.length : 0;
  const feeRows = data.processor_fees ? data.processor_fees.length : 0;
  const rowHeight = 30;
  const maxRows = Math.max(transactionRows, feeRows);
  const totalRows = maxRows + 2;
  const extraSpace = 120;
  return Math.min(Math.max(totalRows * rowHeight + extraSpace, 400), 3500);
};

const loadingList = ref(false);

// --------------------------------
// Payouts Tab Setup
// --------------------------------
const showModal = ref(false);
const selectedGroup = ref({});
const settledTransactions = ref([]);
const payoutsRowData = ref([]);
const defaultColDef = ref({
  flex: 1,
  resizable: true,
  sortable: true,
  filter: true,
});

// Side Bar Configuration for Payouts Grid
const payoutsSideBar = {
  hiddenByDefault: true,
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
    },
  ],
};

let payoutsGridApi = null;
const statementGridApi = shallowRef(null);

// Function to fetch payouts data with optional merchant filter
const fetchPayoutsData = () => {
  loadingPayouts.value = true;
  updateLoadingState();

  let endpoint = '/api/payouts';
  if (payoutsMerchantId.value) {
    endpoint = `${endpoint}?merchant_id=${encodeURIComponent(payoutsMerchantId.value)}`;
  }

  http.get(endpoint).then((response) => {
    payoutsRowData.value = response.data.message;
    loadingPayouts.value = false;
    updateLoadingState();
  });
};

const onPayoutsGridReady = (params) => {
  loadingPayouts.value = true;
  updateLoadingState();
  payoutsGridApi = params.api;
  fetchPayoutsData();
};

const loadingPayouts = ref(false);
const loadingStatements = ref(false);

function updateLoadingState() {
  loadingList.value = loadingPayouts.value || loadingStatements.value;
}

const onPayoutsFirstDataRendered = () => {
  loadingPayouts.value = false;
  updateLoadingState();
};

const onModalGridReady = (params) => {
  params.api.sizeColumnsToFit();
};

const totalAuthAmount = computed(() => {
  if (!selectedGroup.value.transaction_details) {
    return 0;
  }
  return selectedGroup.value.transaction_details
      .reduce((sum, transaction) => {
        return sum + (parseFloat(transaction.net_amount) || 0);
      }, 0)
      .toFixed(2);
});

const columnDefinitionsMainTable = [
  {
    headerName: 'Payout Activity',
    children: [
      {
        headerName: 'Merchant',
        field: 'merchant_name',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Date',
        field: 'payout_date',
        filter: 'agDateColumnFilter',
        sort: 'desc',

        // Turn the API string into a Date object for AG Grid
        valueGetter: (params) => {
          const s = params.data?.payout_date;
          if (!s) return null;

          // Your backend sends "2026-01-05T00:00:00" (no timezone).
          // Append 'Z' so it parses consistently as UTC midnight.
          return new Date(s.endsWith('Z') ? s : `${s}Z`);
        },

        // What users actually see
        valueFormatter: (params) => {
          const d = params.value;
          return d instanceof Date && !isNaN(d) ? d.toLocaleDateString() : '';
        },

        // Make the date filter comparator work with Date objects
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            // compare by date only
            const cellMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
            if (cellMidnight < filterLocalDateAtMidnight) return -1;
            if (cellMidnight > filterLocalDateAtMidnight) return 1;
            return 0;
          },
        },
      },
      {
        headerName: 'Total Amount',
        field: 'payout_details.amount',
        filter: 'agNumberColumnFilter',
        aggFunc: 'sum',
        valueFormatter: (params) => Number(params.value).toFixed(2),
      },
      {
        headerName: 'Processor',
        field: 'payout_details.processor',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Status',
        field: 'payout_details.status',
        filter: 'agTextColumnFilter',
      },
    ],
  },
  {
    headerName: 'Actions',
    children: [
      {
        headerName: 'Transactions',
        field: 'actions',
        cellRenderer: (params) => {
          if (!params.data.transaction_details) return 'N/A';
          return `<p style="font-size: 12px; text-decoration: underline; cursor: pointer;">View Detail</p>`;
        },
        onCellClicked: (params) => {
          selectedGroup.value = params.data;
          settledTransactions.value = params.data.transaction_details || [];
          showModal.value = true;
        },
        sortable: false,
        filter: false,
      },
    ],
  },
];

const columnDefinitionsModal = [
  {
    headerName: 'TransactionId',
    field: 'id',
  },
  {
    headerName: 'Created At',
    field: 'transaction_date',
  },
  {
    headerName: 'Auth Amount',
    field: 'auth_amount',
    aggFunc: 'sum',
    valueFormatter: (params) => parseFloat(params.value).toFixed(2),
  },
  {
    headerName: 'Net Amount',
    field: 'net_amount',
    valueFormatter: (params) => parseFloat(params.value).toFixed(2),
  },
  {
    headerName: 'Transaction Description',
    field: 'trans_description',
  },
];

// --------------------------------
// Statement Tab Setup
// --------------------------------
const themeClass = 'ag-theme-balham';
const statementColumnDefs = ref([
  {
    headerName: 'Merchant Name',
    field: 'merchant_name',
    cellRenderer: 'agGroupCellRenderer',
  },
  {
    headerName: 'Month',
    field: 'month_year',
  },
  {
    headerName: 'Total Amount',
    field: 'total_amount',
    valueFormatter: (params) => `$${parseFloat(params.value).toFixed(2)}`,
    sort: 'desc',
  },
  {
    headerName: 'Total Transactions',
    field: 'total_transactions',
  },
  {
    headerName: 'Total Fees',
    field: 'total_fees',
    valueFormatter: (params) => `$${parseFloat(params.value).toFixed(2)}`,
  },
  {
    headerName: 'Total Adjustments',
    field: 'total_adjustments',
    valueFormatter: (params) => `$${parseFloat(params.value).toFixed(2)}`,
  },
  {
    headerName: 'Chargebacks Total',
    field: 'charge_backs_and_reversals_total',
    valueFormatter: (params) => `$${parseFloat(params.value).toFixed(2)}`,
  },
]);

const statementRowData = ref([]);
const gridApi = shallowRef(null);

const yearMonthOptions = ref([]);
const yearMonth = ref('');

const statementTotalFields = [
  'total_amount',
  'total_transactions',
  'total_fees',
  'total_adjustments',
  'charge_backs_and_reversals_total',
];

function toNumber(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildDailyTransactionTotals(statements) {
  const dailyTotals = new Map();

  statements.forEach((statement) => {
    (statement.daily_transactions || []).forEach((transaction) => {
      const key = transaction.transaction_day;
      const totals = dailyTotals.get(key) || {
        transaction_day: key,
        total_transactions: 0,
        total_processed: 0,
        total_fees: 0,
        total_adjustments: 0,
        total_chargebacks: 0,
      };

      totals.total_transactions += toNumber(transaction.total_transactions);
      totals.total_processed += toNumber(transaction.total_processed);
      totals.total_fees += toNumber(transaction.total_fees);
      totals.total_adjustments += toNumber(transaction.total_adjustments);
      totals.total_chargebacks += toNumber(transaction.total_chargebacks);
      dailyTotals.set(key, totals);
    });
  });

  return Array.from(dailyTotals.values()).sort((a, b) => new Date(b.transaction_day) - new Date(a.transaction_day));
}

function buildProcessorFeeTotals(statements) {
  const feeTotals = new Map();

  statements.forEach((statement) => {
    (statement.processor_fees || []).forEach((fee) => {
      const key = [
        fee.STATEMENT_DATE,
        fee.DETAIL_DESCRIPTION,
        fee.R_RATE1,
        fee.R_RATE2,
      ].join('|');
      const totals = feeTotals.get(key) || {
        STATEMENT_DATE: fee.STATEMENT_DATE,
        DETAIL_DESCRIPTION: fee.DETAIL_DESCRIPTION,
        ITEM_COUNT: 0,
        VOLUME: 0,
        R_RATE1: fee.R_RATE1,
        R_RATE2: fee.R_RATE2,
        R_TOTAL: 0,
      };

      totals.ITEM_COUNT += toNumber(fee.ITEM_COUNT);
      totals.VOLUME += toNumber(fee.VOLUME);
      totals.R_TOTAL += toNumber(fee.R_TOTAL);
      feeTotals.set(key, totals);
    });
  });

  return Array.from(feeTotals.values()).sort((a, b) => new Date(b.STATEMENT_DATE) - new Date(a.STATEMENT_DATE));
}

function buildAllMerchantStatement(statements) {
  const summary = {
    ...statements[0],
    is_summary: true,
    merchant_name: 'All Merchants',
    merchant_id: '',
    daily_transactions: buildDailyTransactionTotals(statements),
    processor_fees: buildProcessorFeeTotals(statements),
  };

  statementTotalFields.forEach((field) => {
    summary[field] = statements.reduce((sum, statement) => sum + toNumber(statement[field]), 0);
  });

  return summary;
}

function normalizeStatementRows(statements) {
  if (merchantId.value || !Array.isArray(statements) || statements.length <= 1) {
    return statements;
  }

  const hasAllMerchantSummary = statements.some((statement) => {
    return statement.is_summary || statement.merchant_name === 'All Merchants';
  });

  if (hasAllMerchantSummary) {
    return statements;
  }

  return [buildAllMerchantStatement(statements), ...statements];
}

function postSortStatementRows(params) {
  const summaryIndex = params.nodes.findIndex((node) => node.data?.is_summary);

  if (summaryIndex > 0) {
    const summaryNode = params.nodes.splice(summaryIndex, 1)[0];
    params.nodes.unshift(summaryNode);
  }
}

// Helper for last month
function getPreviousYearMonth() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

// Generate list of year-month pairs for 2 years
function generateYearMonthOptions() {
  const options = [];
  const today = new Date();
  const startDate = new Date(today.getFullYear() - 2, today.getMonth(), 1);

  while (startDate <= today) {
    const y = startDate.getFullYear();
    const m = String(startDate.getMonth() + 1).padStart(2, '0');
    options.push(`${y}-${m}`);
    startDate.setMonth(startDate.getMonth() + 1);
  }

  // Reverse so most recent appear first
  options.reverse();
  yearMonthOptions.value = options;

  if (!yearMonth.value) {
    yearMonth.value = getPreviousYearMonth();
  }
}

// Fetch statement data from /api/statement
function fetchStatementData() {
  if (!yearMonth.value) {
    yearMonth.value = getPreviousYearMonth();
  }
  loadingStatements.value = true;
  updateLoadingState();

  // *** Only necessary fix: use merchant_id instead of merchant-id ***
  let queryParams = `year-month=${encodeURIComponent(yearMonth.value)}`;
  if (merchantId.value) {
    queryParams += `&merchant_id=${encodeURIComponent(merchantId.value)}`;
  }

  http.get(`/api/statement?${queryParams}`).then((response) => {
    statementRowData.value = normalizeStatementRows(response.data.message);
    loadingStatements.value = false;
    updateLoadingState();
  });
}

// Watch changes in merchantId -> refetch
watch(merchantId, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    fetchStatementData();
  }
});

// Watch changes in payoutsMerchantId -> refetch
watch(payoutsMerchantId, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    fetchPayoutsData();
  }
});

onMounted(() => {
  generateYearMonthOptions();
  fetchMerchantNames();
});

const detailCellRendererParams = {
  getDetailRowData: function (params) {
    params.successCallback(params.data);
  },
};

const onStatementGridReady = (params) => {
  statementGridApi.value = params.api;
  fetchStatementData();
};

// --------------------------------
// Export to Excel
// --------------------------------
const exportPayoutsToExcel = async () => {
  // Example code for exporting Payouts to Excel (unchanged)
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Payouts');

  const mainGridColumnDefs = columnDefinitionsMainTable.flatMap((group) => group.children);
  const mainGridHeaders = mainGridColumnDefs.map((colDef) => ({
    header: colDef.headerName,
    key: colDef.field,
    width: 20,
  }));

  worksheet.columns = [
    { header: '', key: 'indent', width: 5 },
    ...mainGridHeaders.map((col) => ({ key: col.key, width: 20 })),
  ];

  const headerStyle = {
    font: { bold: true, color: { argb: 'FFFFFFFF' } },
    alignment: { horizontal: 'center' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
  };
  const masterDataStyle = {
    font: { bold: true },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } },
  };
  const sectionHeaderStyle = {
    font: { bold: true },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } },
  };
  const sectionDataHeaderStyle = {
    font: { bold: true },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8CBAD' } },
  };
  const sectionDataStyle = {
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } },
  };

  const headerRow = worksheet.addRow(['', ...mainGridHeaders.map((col) => col.header)]);
  headerRow.eachCell((cell, colNumber) => {
    if (colNumber > 1) {
      Object.assign(cell, { style: headerStyle });
    }
  });

  for (const payout of payoutsRowData.value) {
    const rowData = mainGridHeaders.map((colDef) => {
      const field = colDef.key;
      let value;
      if (field.includes('.')) {
        const fields = field.split('.');
        value = payout;
        fields.forEach((f) => {
          value = value ? value[f] : '';
        });
      } else {
        value = payout[field];
      }
      return colDef.valueFormatter ? colDef.valueFormatter({ value }) : value;
    });

    const masterRow = worksheet.addRow(['', ...rowData]);
    masterRow.eachCell((cell, colNumber) => {
      if (colNumber > 1) {
        Object.assign(cell, { style: masterDataStyle });
      }
    });

    worksheet.addRow([]);

    const transactionDetails = payout.transaction_details || [];
    if (transactionDetails.length > 0) {
      const transDetailsHeaderRow = worksheet.addRow(['', 'Transaction Details']);
      transDetailsHeaderRow.getCell(2).style = sectionHeaderStyle;

      const transDetailsHeaders = [
        '',
        'Transaction ID',
        'Transaction Date',
        'Auth Amount',
        'Net Amount',
        'Transaction Description',
      ];
      const transDetailsHeaderRow2 = worksheet.addRow(transDetailsHeaders);
      transDetailsHeaderRow2.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          Object.assign(cell, { style: sectionDataHeaderStyle });
        }
      });

      transactionDetails.forEach((row) => {
        const rowData = [
          '',
          row.id,
          new Date(row.transaction_date).toLocaleDateString(),
          parseFloat(row.auth_amount),
          parseFloat(row.net_amount),
          row.trans_description,
        ];
        const dataRow = worksheet.addRow(rowData);
        dataRow.eachCell((cell, colNumber) => {
          if (colNumber > 1) {
            Object.assign(cell, { style: sectionDataStyle });
            if (['Auth Amount', 'Net Amount'].includes(transDetailsHeaders[colNumber - 1])) {
              cell.numFmt = '"$"#,##0.00';
            }
          }
        });
      });

      worksheet.addRow([]);
    }

    worksheet.addRow([]);
  }

  worksheet.columns.forEach((column) => {
    if (!column.width || column.width < 15) {
      column.width = 15;
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'Payouts.xlsx');
  });
};

const exportStatementToExcel = async () => {
  // Example code for exporting Statement to Excel (unchanged)
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Statement');

  const mainGridColumnDefs = statementGridApi.value.getColumnDefs();
  const mainGridHeaders = mainGridColumnDefs.map((colDef) => ({
    header: colDef.headerName,
    key: colDef.field,
    width: 20,
  }));

  worksheet.columns = [
    { header: '', key: 'indent', width: 5 },
    ...mainGridHeaders.map((col) => ({ key: col.key, width: 20 })),
  ];

  const headerStyle = {
    font: { bold: true, color: { argb: 'FFFFFFFF' } },
    alignment: { horizontal: 'center' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
  };
  const masterDataStyle = {
    font: { bold: true },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } },
  };
  const sectionHeaderStyle = {
    font: { bold: true },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } },
  };
  const sectionDataHeaderStyle = {
    font: { bold: true },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8CBAD' } },
  };
  const sectionDataStyle = {
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } },
  };

  const headerRow = worksheet.addRow(['', ...mainGridHeaders.map((col) => col.header)]);
  headerRow.eachCell((cell, colNumber) => {
    if (colNumber > 1) {
      Object.assign(cell, { style: headerStyle });
    }
  });

  for (const statement of statementRowData.value) {
    const rowData = mainGridHeaders.map((colDef) => {
      const field = colDef.key;
      const value = statement[field];
      return colDef.valueFormatter ? colDef.valueFormatter({ value }) : value;
    });

    const masterRow = worksheet.addRow(['', ...rowData]);
    masterRow.eachCell((cell, colNumber) => {
      if (colNumber > 1) {
        Object.assign(cell, { style: masterDataStyle });
      }
    });

    worksheet.addRow([]);

    const dailyTransactions = statement.daily_transactions || [];
    if (dailyTransactions.length > 0) {
      const dailyTransHeaderRow = worksheet.addRow(['', 'Daily Transactions']);
      dailyTransHeaderRow.getCell(2).style = sectionHeaderStyle;

      const dailyTransHeaders = [
        '',
        'Transaction Day',
        'Total Transactions',
        'Total Processed',
        'Total Fees',
        'Total Adjustments',
        'Total Chargebacks',
      ];
      const dailyTransHeaderRow2 = worksheet.addRow(dailyTransHeaders);
      dailyTransHeaderRow2.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          Object.assign(cell, { style: sectionDataHeaderStyle });
        }
      });

      dailyTransactions.forEach((row) => {
        const rowData = [
          '',
          new Date(row.transaction_day + 'T12:00:00').toLocaleDateString(),
          row.total_transactions,
          parseFloat(row.total_processed),
          parseFloat(row.total_fees),
          parseFloat(row.total_adjustments),
          parseFloat(row.total_chargebacks),
        ];
        const dataRow = worksheet.addRow(rowData);
        dataRow.eachCell((cell, colNumber) => {
          if (colNumber > 1) {
            Object.assign(cell, { style: sectionDataStyle });
            if (
                ['Total Processed', 'Total Fees', 'Total Adjustments', 'Total Chargebacks'].includes(
                    dailyTransHeaders[colNumber - 1]
                )
            ) {
              cell.numFmt = '"$"#,##0.00';
            } else if (dailyTransHeaders[colNumber - 1] === 'Total Transactions') {
              cell.numFmt = '0';
            }
          }
        });
      });

      worksheet.addRow([]);
    }

    const processorFees = statement.processor_fees || [];
    if (processorFees.length > 0) {
      const procFeesHeaderRow = worksheet.addRow(['', 'Processor Fees']);
      procFeesHeaderRow.getCell(2).style = sectionHeaderStyle;

      const procFeesHeaders = [
        '',
        'Statement Date',
        'Description',
        'Item Count',
        'Volume',
        'Rate 1',
        'Rate 2',
        'Total',
      ];
      const procFeesHeaderRow2 = worksheet.addRow(procFeesHeaders);
      procFeesHeaderRow2.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          Object.assign(cell, { style: sectionDataHeaderStyle });
        }
      });

      processorFees.forEach((row) => {
        const rowData = [
          '',
          new Date(row.STATEMENT_DATE).toLocaleDateString(),
          row.DETAIL_DESCRIPTION,
          row.ITEM_COUNT,
          parseFloat(row.VOLUME),
          parseFloat(row.R_RATE1),
          parseFloat(row.R_RATE2),
          parseFloat(row.R_TOTAL),
        ];
        const dataRow = worksheet.addRow(rowData);
        dataRow.eachCell((cell, colNumber) => {
          if (colNumber > 1) {
            Object.assign(cell, { style: sectionDataStyle });
            const headerText = procFeesHeaders[colNumber - 1];
            if (['Item Count'].includes(headerText)) {
              cell.numFmt = '0';
            } else if (['Volume', 'Rate 1', 'Rate 2'].includes(headerText)) {
              cell.numFmt = '#,##0.00';
            }
          }
        });
      });

      worksheet.addRow([]);
    }

    worksheet.addRow([]);
  }

  worksheet.columns.forEach((column) => {
    if (!column.width || column.width < 15) {
      column.width = 15;
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'Statement.xlsx');
  });
};

// -------------------------------------------
// DOWNLOAD STATEMENT PDF
// -------------------------------------------
const downloadStatementPDF = async () => {
  try {
    // *** Only necessary fix: use merchant_id instead of merchant-id ***
    const queryParams = new URLSearchParams({
      merchant_id: merchantId.value, // could be empty string if "All Merchants"
      'year-month': yearMonth.value,
    }).toString();

    // Make the GET request for PDF
    const { data } = await http.get(`/api/statement/pdf?${queryParams}`, {
      responseType: 'blob', // get raw binary data
    });

    const blob = new Blob([data], { type: 'application/pdf' });
    const filename = `Statement-${merchantId.value || 'All'}-${yearMonth.value}.pdf`;
    saveAs(blob, filename);
  } catch (error) {
    console.error('Error downloading PDF:', error);
  }
};
</script>

<style lang="scss" scoped>
[v-cloak] {
  display: none;
}

.loader {
  z-index: 2;
}

.table {
  height: 70vh !important;
}

.subs-btn {
  height: 40px;
  padding: 0;
  background: #fff;
  border-color: #f6951e !important;
  color: #f6951e !important;

  &:hover {
    background: var(--c-grey-light) !important;
  }
}

@media screen and (max-width: 768px) {
  .reporting-filters {
    flex-direction: column;
  }

  .reporting-filters-container {
    flex-direction: column-reverse;
  }

  .report-input {
    width: 50%;

    &:last-child {
      margin-right: 0 !important;
    }
  }
}

::v-deep(.report-buttons) {
  @media screen and (max-width: 768px) {
    .btn {
      width: 50%;
    }
  }
}
</style>

<style lang="scss">
@import "ag-grid-community/styles/ag-grid.css";
@import "ag-grid-community/styles/ag-theme-balham.min.css";

/* Additional styles for the reporting page */
.reporting {
  .status-value {
    &.Failed {
      background: #ffe4e4;
      color: #b3322c;
    }

    &.Success {
      color: var(--c-success);
      background: #ddffef;
    }
  }

  .actions {
    height: 100% !important;
  }

  .action-btn {
    line-height: initial;
    width: 30px !important;
    height: 30px !important;
    padding: 2px 1px !important;
    font-size: 14px !important;
    border-radius: 50% !important;

    .icon-container {
      width: auto !important;

      svg {
        width: 19px !important;
      }
    }

    &.success {
      color: #fff !important;
    }
  }
}
</style>
